import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Send, Check } from "lucide-react";
import { COMPANY, SERVICE_OPTIONS } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-none border border-white/15 bg-secondary px-4 py-4 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors focus:border-primary";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/service-requests`, {
        ...form,
        service: form.service || "Not sure",
      });
      setDone(true);
      toast.success("Request sent — we'll call you back shortly.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative border-t border-white/10 bg-background py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="mb-4 flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.3em] text-primary">
              <span className="inline-block h-2 w-2 bg-primary" /> Get in touch
            </div>
            <h2 className="font-display text-6xl uppercase leading-[0.85] tracking-tight md:text-7xl">
              Book a <br /> <span className="text-stroke-primary">service call</span>
            </h2>
            <p className="mt-6 max-w-md text-sm text-muted-foreground md:text-base">
              Tell us what's going on and the fastest way to reach you. For
              emergencies, calling is always quickest.
            </p>

            <div className="mt-10 space-y-px border border-white/10 bg-white/10">
              <a
                href={COMPANY.phoneHref}
                data-testid="contact-phone"
                className="group flex items-center gap-4 bg-background p-6 transition-colors hover:bg-secondary"
              >
                <Phone className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                    Phone
                  </div>
                  <div className="font-display text-2xl uppercase tracking-tight">{COMPANY.phone}</div>
                </div>
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email"
                className="group flex items-center gap-4 bg-background p-6 transition-colors hover:bg-secondary"
              >
                <Mail className="h-5 w-5 text-primary" />
                <div className="min-w-0">
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                    Email
                  </div>
                  <div className="break-all text-sm sm:text-base">{COMPANY.email}</div>
                </div>
              </a>
            </div>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="contact-success"
                className="flex h-full min-h-[420px] flex-col items-center justify-center border border-primary/40 bg-secondary p-10 text-center"
              >
                <div className="flex h-16 w-16 items-center justify-center bg-primary text-primary-foreground">
                  <Check className="h-8 w-8" />
                </div>
                <h3 className="mt-6 font-display text-4xl uppercase tracking-tight">Request received</h3>
                <p className="mt-3 max-w-sm text-sm text-muted-foreground">
                  Thanks — a Maddox technician will reach out shortly. Need help
                  right now? Call {COMPANY.phone}.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="contact-reset"
                  className="mt-8 border border-white/20 px-6 py-3 font-mono-tech text-xs uppercase tracking-[0.15em] transition-colors hover:border-primary hover:text-primary"
                >
                  Send another
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="service-request-form"
                className="border border-white/10 bg-secondary/40 p-6 md:p-10"
              >
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      Name *
                    </label>
                    <input
                      data-testid="input-name"
                      className={field}
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      Phone *
                    </label>
                    <input
                      data-testid="input-phone"
                      className={field}
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="(503) 555-0000"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      Email
                    </label>
                    <input
                      data-testid="input-email"
                      className={field}
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@email.com"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      Service
                    </label>
                    <select
                      data-testid="input-service"
                      className={`${field} appearance-none`}
                      value={form.service}
                      onChange={set("service")}
                    >
                      <option value="">Select a service</option>
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-5">
                  <label className="mb-2 block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    What's happening?
                  </label>
                  <textarea
                    data-testid="input-message"
                    rows={4}
                    className={field}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Furnace won't turn on, strange noise from the AC..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-service-request"
                  className="mt-7 flex w-full items-center justify-center gap-3 bg-primary py-5 font-mono-tech text-sm font-bold uppercase tracking-[0.15em] text-primary-foreground transition-colors hover:bg-white hover:text-black disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send Request"} <Send className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
